import { apiClient, type PageData } from './client'
import type { Order, OrderCartInfo, OrderStatus } from '@/schemas/supplier'

export interface EmployeeManualOrder extends Order {
  carrot_user_id?: number
  pay_type?: string
  creator_id?: number
  creator_name?: string
}

export interface EmployeeManualOrderListParams {
  page: number
  page_size: number
  order_no?: string
  real_name?: string
  delivery_id?: string
  status?: number | string
  date?: string
  data?: string
  scope?: 'my' | 'department'
  department_id?: number
  is_store?: number
  is_cash?: number
  is_new?: number
  shipping_partial?: number
  creator_ids?: number[]
}

export interface EmployeeManualOrderExportFilterParams
  extends Omit<EmployeeManualOrderListParams, 'status'> {
  status?: number
}

export interface EmployeeManualOrderExportRequest {
  mode: 'filter' | 'selected'
  ids?: number[]
  filter?: EmployeeManualOrderExportFilterParams
}

export interface ManualOrderRefundEligibilityItem {
  cart_id: string
  product_id: number
  product_name: string
  product_image: string
  product_attr: string
  cart_num: number
  refunded_num: number
  refundable_num: number
  unit_refund_price: number
  max_refund_price: number
  is_support_refund: number
}

export interface ManualOrderRefundEligibility {
  can_apply: boolean
  reason: string
  max_refund_price: number
  items: ManualOrderRefundEligibilityItem[]
}

export interface EmployeeManualOrderDetailResponse {
  order: EmployeeManualOrder
  cart_infos?: OrderCartInfo[]
  statuses?: OrderStatus[]
  refund_eligibility?: ManualOrderRefundEligibility
}

export interface EmployeeManualOrderDetail {
  order: EmployeeManualOrder
  cart_infos: OrderCartInfo[]
  statuses: OrderStatus[]
  refund_eligibility?: ManualOrderRefundEligibility
}

export type PaymentQrcodeStatus = 'available' | 'no_department' | 'no_qrcode'

export interface EmployeeManualOrderPaymentQrcode {
  order_id: string
  order_numeric_id: number
  pay_price: number
  real_name: string
  user_phone: string
  product_summary: string
  total_num: number
  payment_qrcode_status: PaymentQrcodeStatus
  payment_qrcode: string
  payment_department_id: number
  payment_department_name: string
}

export interface ConfirmManualOrderPaymentParams {
  remark?: string
  payment_reference?: string
}

export interface ConfirmManualOrderPaymentResult {
  id: number
  order_id: string
  paid: number
  pay_type: string
  pay_time: number
}

export interface ManualOrderApplyRefundParams {
  refund_reason: string
  refund_explain?: string
  refund_price: number
  cart_ids: Array<{ cart_id: string; cart_num: number }>
}

export interface ManualOrderMarkRefundReturnedParams {
  refund_express: string
  refund_express_name: string
  refund_phone?: string
  refund_explain?: string
}

export interface ManualOrderApplyRefundResult {
  id: number
  order_id: string
  store_order_id: number
  refund_price: number
  refund_num: number
  refund_type: number
  apply_type: number
}

export interface ManualOrderDeliveryParams {
  delivery_name: string
  delivery_id: string
  delivery_code: string
}

export interface ManualOrderDeliveryImportRow {
  row: number
  order_id: string
  success: boolean
  message: string
}

export interface ManualOrderDeliveryImportResult {
  total: number
  success: number
  failed: number
  rows: ManualOrderDeliveryImportRow[]
}

export type EmployeeManualOrderImportParams = Pick<
  EmployeeManualOrderListParams,
  'scope' | 'department_id'
>

export interface ManualOrderShippingListItem {
  cart_id: string
  product_name: string
  product_attr: string
  cart_num: number
}

export interface ManualOrderShippingListOrder {
  id: number
  order_id: string
  real_name: string
  user_phone: string
  user_address: string
  items: ManualOrderShippingListItem[]
}

export interface ManualOrderShippingListSkipped {
  id: number
  reason: string
}

export interface ManualOrderShippingListPreview {
  orders: ManualOrderShippingListOrder[]
  skipped: ManualOrderShippingListSkipped[]
}

export interface ManualOrderTrackingEvent {
  time: string
  context: string
}

export interface ManualOrderTracking {
  state: string
  com: string
  nu: string
  timeline: ManualOrderTrackingEvent[]
}

export const employeeManualOrderApi = {
  /** 获取员工手动订单列表 */
  list: async (params: EmployeeManualOrderListParams): Promise<PageData<EmployeeManualOrder>> => {
    const searchParams = new URLSearchParams()

    searchParams.set('page', String(params.page || 1))
    searchParams.set('page_size', String(params.page_size || 30))

    if (params.order_no) searchParams.set('order_no', params.order_no)
    if (params.real_name) searchParams.set('real_name', params.real_name)
    if (params.delivery_id) searchParams.set('delivery_id', params.delivery_id)
    if (params.status !== undefined && params.status !== '') {
      searchParams.set('status', String(params.status))
    }
    if (params.date) searchParams.set('date', params.date)
    if (params.data) searchParams.set('data', params.data)
    if (params.scope) searchParams.set('scope', params.scope)
    const departmentID = params.department_id
    if (
      params.scope === 'department' &&
      typeof departmentID === 'number' &&
      Number.isInteger(departmentID) &&
      departmentID > 0
    ) {
      searchParams.set('department_id', String(departmentID))
    }
    if (params.is_store !== undefined) searchParams.set('is_store', String(params.is_store))
    if (params.is_cash !== undefined) searchParams.set('is_cash', String(params.is_cash))
    if (params.is_new !== undefined) searchParams.set('is_new', String(params.is_new))
    if (params.shipping_partial !== undefined) searchParams.set('shipping_partial', String(params.shipping_partial))
    if (params.creator_ids && params.creator_ids.length > 0) {
      searchParams.set('creator_ids', params.creator_ids.join(','))
    }

    const response = await apiClient.get<PageData<EmployeeManualOrder>>(
      `/employee/manual-orders?${searchParams.toString()}`
    )
    return response.data
  },

  /** 获取员工手动订单详情 */
  detail: async (id: number, scope?: string): Promise<EmployeeManualOrderDetail> => {
    const searchParams = new URLSearchParams()
    if (scope) searchParams.set('scope', scope)
    const query = searchParams.toString()
    const response = await apiClient.get<EmployeeManualOrderDetailResponse>(
      `/employee/manual-orders/${id}${query ? `?${query}` : ''}`
    )
    const data = response.data
    const cartInfos = data.cart_infos || []
    const order = {
      ...data.order,
      cart_info: cartInfos.length > 0 ? cartInfos : data.order.cart_info,
    }

    return {
      order,
      cart_infos: cartInfos,
      statuses: data.statuses || [],
      refund_eligibility: data.refund_eligibility,
    }
  },

  /** 获取员工手动订单收款码 */
  paymentQrcode: async (id: number): Promise<EmployeeManualOrderPaymentQrcode> => {
    const response = await apiClient.get<EmployeeManualOrderPaymentQrcode>(
      `/employee/manual-orders/${id}/payment-qrcode`
    )
    return response.data
  },

  /** 确认员工手动订单线下收款 */
  confirmPayment: async (
    id: number,
    data: ConfirmManualOrderPaymentParams = {}
  ): Promise<ConfirmManualOrderPaymentResult> => {
    const response = await apiClient.post<ConfirmManualOrderPaymentResult>(
      `/employee/manual-orders/${id}/confirm-payment`,
      data
    )
    return response.data
  },

  /** 申请员工手动订单退货 */
  applyRefund: async (
    id: number,
    data: ManualOrderApplyRefundParams,
    scope?: string
  ): Promise<ManualOrderApplyRefundResult> => {
    const searchParams = new URLSearchParams()
    if (scope) searchParams.set('scope', scope)
    const query = searchParams.toString()
    const response = await apiClient.post<ManualOrderApplyRefundResult>(
      `/employee/manual-orders/${id}/refund${query ? `?${query}` : ''}`,
      data
    )
    return response.data
  },

  /** 录入人员代用户登记退货 */
  markRefundReturned: async (
    id: number,
    data: ManualOrderMarkRefundReturnedParams,
    scope?: string
  ): Promise<void> => {
    const searchParams = new URLSearchParams()
    if (scope) searchParams.set('scope', scope)
    const query = searchParams.toString()
    await apiClient.post(`/employee/manual-orders/${id}/refund/return${query ? `?${query}` : ''}`, data)
  },

  /** 取消员工手动订单 */
  cancel: async (id: number): Promise<void> => {
    await apiClient.post(`/employee/manual-orders/${id}/cancel`)
  },

  /** 员工手动订单发货 */
  delivery: async (id: number, data: ManualOrderDeliveryParams): Promise<void> => {
    await apiClient.put(`/employee/manual-orders/${id}/delivery`, data)
  },

  /** 查询员工手动订单物流轨迹 */
  getTracking: async (id: number, scope?: string): Promise<ManualOrderTracking> => {
    const searchParams = new URLSearchParams()
    if (scope) searchParams.set('scope', scope)
    const query = searchParams.toString()
    const response = await apiClient.get<ManualOrderTracking>(
      `/employee/manual-orders/${id}/tracking${query ? `?${query}` : ''}`
    )
    return response.data
  },

  /** 导入员工手动订单发货 Excel */
  importDeliveryExcel: async (
    file: File,
    params?: EmployeeManualOrderImportParams
  ): Promise<ManualOrderDeliveryImportResult> => {
    const formData = new FormData()
    formData.append('file', file)
    if (params?.scope) {
      formData.append('scope', params.scope)
    }
    const departmentID = params?.department_id
    if (
      typeof departmentID === 'number' &&
      Number.isInteger(departmentID) &&
      departmentID > 0
    ) {
      formData.append('department_id', String(departmentID))
    }
    const response = await apiClient.upload<ManualOrderDeliveryImportResult>(
      '/employee/manual-orders/import',
      formData
    )
    return response.data
  },

  /** 预览员工手动订单发货清单 */
  previewShippingLists: async (ids: number[]): Promise<ManualOrderShippingListPreview> => {
    const response = await apiClient.post<ManualOrderShippingListPreview>(
      '/employee/manual-orders/shipping-lists/preview',
      { ids }
    )
    return response.data
  },

  /** 获取服务端渲染的打印 HTML */
  fetchShippingListHTML: async (ids: number[]): Promise<string> => {
    return apiClient.fetchRawText('/employee/manual-orders/shipping-lists/print', { ids })
  },

  /** 导出员工手动订单 Excel */
  exportExcel: async (data: EmployeeManualOrderExportRequest): Promise<void> => {
    await apiClient.download('/employee/manual-orders/export', data)
  },

  /** 删除员工手动订单 */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/employee/manual-orders/${id}`)
  },

  /** 批量删除员工手动订单 */
  batchDelete: async (ids: number[]): Promise<void> => {
    await apiClient.post('/employee/manual-orders/batch-delete', { ids })
  },

  /** 更新手动订单标记 */
  updateFlags: async (id: number, params: {
    is_store: boolean
    is_cash: boolean
    shipping_partial: boolean
    shipping_remark: string
  }): Promise<EmployeeManualOrderDetail> => {
    const response = await apiClient.put<EmployeeManualOrderDetailResponse>(
      `/employee/manual-orders/${id}/flags`,
      params
    )
    const data = response.data
    const cartInfos = data.cart_infos || []
    const order = {
      ...data.order,
      cart_info: cartInfos.length > 0 ? cartInfos : data.order.cart_info,
    }
    return {
      order,
      cart_infos: cartInfos,
      statuses: data.statuses || [],
      refund_eligibility: data.refund_eligibility,
    }
  },

  /** 更新手动订单收货信息（收货人/电话/地址） */
  updateShipping: async (id: number, params: {
    real_name: string
    user_phone: string
    user_address: string
  }): Promise<EmployeeManualOrderDetail> => {
    const response = await apiClient.put<EmployeeManualOrderDetailResponse>(
      `/employee/manual-orders/${id}/shipping`,
      params
    )
    const data = response.data
    const cartInfos = data.cart_infos || []
    const order = {
      ...data.order,
      cart_info: cartInfos.length > 0 ? cartInfos : data.order.cart_info,
    }
    return {
      order,
      cart_infos: cartInfos,
      statuses: data.statuses || [],
      refund_eligibility: data.refund_eligibility,
    }
  },
}
